import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { chatClient, type Bot, type Session } from '@/services'
import { createLatestRequestManager, isAbortError } from '../lib/requestCancellation'
import { chatTrace } from '../lib/chatTrace'

export const useChatHistoryStore = defineStore('chatHistory', () => {
  const sessions = ref<Session[]>([])
  const loadingSessions = ref(false)
  let activeSessionsLoadRequestId = 0
  const requestManager = createLatestRequestManager()
  const sessionsLoadRequestKey = 'sessions-load'

  interface LoadSessionsOptions {
    botId?: string
  }

  const historyBots = computed<Bot[]>(() => {
    const seen = new Set<string>()
    const bots: Bot[] = []

    for (const session of sessions.value) {
      if (seen.has(session.botId)) continue
      seen.add(session.botId)
      bots.push({
        id: session.botId,
        name: session.botName ?? session.botId,
        type: session.botType ?? 'departmental',
      })
    }

    return bots
  })

  async function loadSessions(options: LoadSessionsOptions = {}) {
    const startedAt = performance.now()
    const requestId = activeSessionsLoadRequestId + 1
    activeSessionsLoadRequestId = requestId
    loadingSessions.value = true
    chatTrace('chatHistoryStore', 'loadSessions.start', {
      requestId,
      botId: options.botId ?? null,
    })
    try {
      const loadedSessions = await requestManager.run(
        sessionsLoadRequestKey,
        signal => chatClient.getSessions(signal, options),
      )
      if (requestId !== activeSessionsLoadRequestId) {
        chatTrace('chatHistoryStore', 'loadSessions.staleResponse', {
          requestId,
          activeRequestId: activeSessionsLoadRequestId,
          botId: options.botId ?? null,
          durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
        })
        return sessions.value
      }
      sessions.value = loadedSessions
      chatTrace('chatHistoryStore', 'loadSessions.success', {
        requestId,
        botId: options.botId ?? null,
        count: loadedSessions.length,
        durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
      })
      return sessions.value
    } catch (err) {
      if (!isAbortError(err)) {
        chatTrace('chatHistoryStore', 'loadSessions.error', {
          requestId,
          botId: options.botId ?? null,
          error: err instanceof Error ? err.message : String(err),
          durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
        })
        throw err
      }
      chatTrace('chatHistoryStore', 'loadSessions.aborted', {
        requestId,
        botId: options.botId ?? null,
        durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
      })
      return sessions.value
    } finally {
      if (requestId === activeSessionsLoadRequestId) {
        loadingSessions.value = false
      }
    }
  }

  function clearSessions() {
    activeSessionsLoadRequestId += 1
    requestManager.cancel(sessionsLoadRequestKey)
    loadingSessions.value = false
    sessions.value = []
  }

  function upsertSession(session: Session) {
    const index = sessions.value.findIndex(existing => existing.id === session.id && existing.botId === session.botId)
    if (index === -1) {
      sessions.value = [session, ...sessions.value]
      return
    }

    const next = [...sessions.value]
    next[index] = session
    sessions.value = next
  }

  function updateSessionTitle(botId: string, sessionId: string, title: string) {
    const session = sessions.value.find(existing => existing.id === sessionId && existing.botId === botId)
    if (!session) return
    session.title = title
    sessions.value = [...sessions.value]
  }

  function getSession(botId: string, sessionId: string): Session | undefined {
    return sessions.value.find(session => session.id === sessionId && session.botId === botId)
  }

  function removeSession(botId: string, sessionId: string) {
    sessions.value = sessions.value.filter(session => !(session.id === sessionId && session.botId === botId))
  }

  async function toggleStar(botId: string, sessionId: string): Promise<{ starred: boolean; limitReached?: boolean }> {
    const result = await chatClient.toggleStar(botId, sessionId)
    if (!result.limitReached) {
      const session = sessions.value.find(existing => existing.id === sessionId && existing.botId === botId)
      if (session) {
        session.starred = result.starred
        sessions.value = [...sessions.value]
      }
    }
    return result
  }

  async function renameSession(botId: string, sessionId: string, title: string): Promise<Session> {
    const updated = await chatClient.updateSession(botId, sessionId, { title })
    upsertSession(updated)
    return updated
  }

  async function deleteSession(botId: string, sessionId: string): Promise<void> {
    await chatClient.deleteSession(botId, sessionId)
    removeSession(botId, sessionId)
  }

  return {
    sessions,
    loadingSessions,
    historyBots,
    loadSessions,
    clearSessions,
    upsertSession,
    updateSessionTitle,
    getSession,
    removeSession,
    toggleStar,
    renameSession,
    deleteSession,
  }
})
